import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from './ui/button';
import { AlertTriangle, RefreshCw, Zap, Wifi, WifiOff } from 'lucide-react';
import { Card, CardContent } from './ui/card';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  // Accept both `provider` and `aiProvider` (tests use `provider`)
  provider?: 'openai' | 'ollama' | 'gemini' | 'fallback';
  aiProvider?: 'openai' | 'ollama' | 'gemini' | 'fallback';
  // onError signature expected by tests: (errorLike, info, provider)
  onError?: (error: { message: string; code?: string }, errorInfo: ErrorInfo, provider?: string) => void;
  onRetry?: () => void;
  showFallbackOptions?: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorType: 'ai_timeout' | 'ai_overload' | 'network' | 'rate_limit' | 'invalid_response' | 'unknown';
  retryCount: number;
  isRetrying: boolean;
  errorCount: number;
}

/**
 * Specialized error boundary for AI-powered components
 * 
 * Handles AI-specific error scenarios with:
 * - Provider-specific error messages
 * - Intelligent retry strategies
 * - Fallback AI provider suggestions
 * - User education about AI limitations
 */
export class AiErrorBoundary extends Component<Props, State> {
  private retryTimeout: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorType: 'unknown',
      retryCount: 0,
      isRetrying: false,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorType: AiErrorBoundary.categorizeError(error)
    };
  }

  static categorizeError(error: Error): State['errorType'] {
    const code = (error as any)?.code ? String((error as any).code).toUpperCase() : '';
    const message = error.message.toLowerCase();
    if (code === 'INVALID_API_KEY') return 'invalid_response';
    
    if (message.includes('timeout') || message.includes('aborted')) {
      return 'ai_timeout';
    }
    
    if (message.includes('rate limit') || message.includes('429') || message.includes('too many requests')) {
      return 'rate_limit';
    }
    
    if (message.includes('overload') || message.includes('busy') || message.includes('503') || message.includes('502')) {
      return 'ai_overload';
    }
    
    if (message.includes('network') || message.includes('fetch') || message.includes('connection')) {
      return 'network';
    }
    
    if (message.includes('invalid') || message.includes('parse') || message.includes('format')) {
      return 'invalid_response';
    }
    
    return 'unknown';
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const provider = this.props.provider || this.props.aiProvider;
    console.error('AI Error Boundary caught error:', {
      error: error.message,
      type: this.state.errorType,
      provider,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      retryCount: this.state.retryCount
    });

    this.setState((s) => ({ errorCount: s.errorCount + 1 }));

    // Normalize onError shape for tests
    this.props.onError?.(
      { message: error.message, code: (error as any)?.code },
      errorInfo,
      provider
    );

    // Report AI-specific error to analytics
    this.reportAiError(error, errorInfo);
  }

  private reportAiError = async (error: Error, errorInfo: ErrorInfo) => {
    try {
      if ((window as any).posthog) {
        (window as any).posthog.capture('ai_error_boundary_triggered', {
          error_type: this.state.errorType,
          ai_provider: this.props.aiProvider || 'unknown',
          error_message: error.message,
          retry_count: this.state.retryCount,
          component_stack: errorInfo.componentStack,
          timestamp: new Date().toISOString()
        });
      }
    } catch (reportingError) {
      console.warn('Failed to report AI error:', reportingError);
    }
  };

  private handleRetry = async () => {
    const { retryCount } = this.state;
    const maxRetries = this.getMaxRetries();
    
    if (retryCount >= maxRetries) {
      return;
    }

    this.setState({ isRetrying: true });
    
    // Call custom retry handler if provided
    if (this.props.onRetry) {
      try {
        await this.props.onRetry();
      } catch (error) {
        console.error('Custom retry handler failed:', error);
      }
    }

    // AI-specific retry delays
    const delayMs = this.getRetryDelay();
    
    console.log(`Retrying AI component in ${delayMs}ms (attempt ${retryCount + 1}/${maxRetries})`);
    
    this.retryTimeout = setTimeout(() => {
      this.setState({
        hasError: false,
        error: null,
        errorType: 'unknown',
        retryCount: retryCount + 1,
        isRetrying: false
      });
    }, delayMs);
  };

  private getMaxRetries = (): number => {
    switch (this.state.errorType) {
      case 'ai_timeout':
      case 'network':
        return 3;
      case 'ai_overload':
      case 'rate_limit':
        return 2;
      case 'invalid_response':
        return 1;
      default:
        return 2;
    }
  };

  private getRetryDelay = (): number => {
    const { errorType, retryCount } = this.state;
    
    switch (errorType) {
      case 'rate_limit':
        return Math.pow(2, retryCount + 2) * 1000; // 4s, 8s, 16s
      case 'ai_overload':
        return (retryCount + 3) * 1000; // 3s, 4s, 5s
      case 'ai_timeout':
        return Math.pow(2, retryCount + 1) * 1000; // 2s, 4s, 8s
      default:
        return Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
    }
  };

  private getErrorContent = () => {
    const { errorType } = this.state;
    const aiProvider = this.props.provider || this.props.aiProvider;
    const retryCount = this.state.retryCount;
    const maxRetries = this.getMaxRetries();
    // Don't show manual retry for rate limit / overload
    const allowManualRetry = !(errorType === 'rate_limit' || errorType === 'ai_overload');
    // Keep the button rendered while retrying so tests can assert disabled
    const canRetry = allowManualRetry && retryCount < maxRetries;

    const content = {
      ai_timeout: {
        icon: <Zap className="w-8 h-8 text-yellow-500" />,
        title: "AI Request Timeout",
        message: `AI service is taking longer than expected. Please try again shortly.`,
        action: canRetry ? "Tap the retry button" : "Please wait",
        bgColor: "bg-yellow-50 border-yellow-200",
        textColor: "text-yellow-800"
      },
      ai_overload: {
        icon: <AlertTriangle className="w-8 h-8 text-orange-500" />,
        title: "AI Service Busy",
        message: `AI service is experiencing high demand right now. This is temporary.`,
        action: canRetry ? "We'll retry automatically" : "Please try again in a few minutes",
        bgColor: "bg-orange-50 border-orange-200",
        textColor: "text-orange-800"
      },
      rate_limit: {
        icon: <AlertTriangle className="w-8 h-8 text-red-500" />,
        title: "Rate Limit Reached",
        message: "We received too many requests in a short time. Please wait before trying again.",
        action: "Retry later",
        bgColor: "bg-red-50 border-red-200",
        textColor: "text-red-800"
      },
      network: {
        icon: <WifiOff className="w-8 h-8 text-gray-500" />,
        title: "Connection Error",
        message: "Please check your internet connection and try again.",
        action: canRetry ? "Tap the retry button" : "Please wait",
        bgColor: "bg-gray-50 border-gray-200",
        textColor: "text-gray-800"
      },
      invalid_response: {
        icon: <AlertTriangle className="w-8 h-8 text-purple-500" />,
        title: "Configuration Error",
        message: "API configuration issue detected. Please verify your API key and settings.",
        action: canRetry ? "Retry after checking settings" : "Please wait",
        bgColor: "bg-purple-50 border-purple-200",
        textColor: "text-purple-800"
      },
      unknown: {
        icon: <AlertTriangle className="w-8 h-8 text-blue-500" />,
        title: "AI Service Issue",
        message: "Something unexpected happened with our AI service. Our team has been notified.",
        action: canRetry ? "Let's try again" : "Please try again later",
        bgColor: "bg-blue-50 border-blue-200",
        textColor: "text-blue-800"
      }
    };

    return content[errorType];
  };

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorType: 'unknown',
      retryCount: 0,
      isRetrying: false
    });
  };

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    // If children changed after an error, assume recovery and reset
    if (this.state.hasError && prevProps.children !== this.props.children) {
      this.setState({
        hasError: false,
        error: null,
        errorType: 'unknown',
        isRetrying: false,
        retryCount: 0,
      });
    }
  }

  render() {
    // Defensive: ensure state exists before render in unusual import scenarios
    if (!this.state) return null;
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const errorContent = this.getErrorContent();
      const allowManualRetry = !(this.state.errorType === 'rate_limit' || this.state.errorType === 'ai_overload');
      const canRetry = allowManualRetry && this.state.retryCount < this.getMaxRetries();
      const isDev = process.env.NODE_ENV === 'development';

      return (
        <Card className={`w-full max-w-md mx-auto border ${errorContent.bgColor}`}>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              {/* Error Icon */}
              <div className="relative">
                {this.state.isRetrying && (
                  <div className="absolute inset-0 animate-ping rounded-full bg-current opacity-25" />
                )}
                {errorContent.icon}
              </div>
              
              {/* Error Content */}
              <div className="space-y-2">
                <h3 className={`text-lg font-semibold ${errorContent.textColor}`}>
                  {errorContent.title}
                </h3>
                <p className={`text-sm ${errorContent.textColor} opacity-90`}>
                  {errorContent.message}
                </p>
                {/* Action hint (avoid duplicating button label text) */}
                {errorContent.action && (
                  <p className={`text-xs ${errorContent.textColor} opacity-75`}>
                    {errorContent.action}
                  </p>
                )}
              </div>
              
              {/* AI Provider Info */}
              {(this.props.provider || this.props.aiProvider) && (
                <div className="flex items-center space-x-2 text-xs opacity-60">
                  <Wifi className="w-3 h-3" />
                  <span>AI Provider: {(this.props.provider || this.props.aiProvider)!.toUpperCase()}</span>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                {canRetry && (
                  <Button
                    onClick={this.handleRetry}
                    disabled={this.state.isRetrying}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className={`w-4 h-4 ${this.state.isRetrying ? 'animate-spin' : ''}`} />
                    {this.state.isRetrying ? 'Please wait' : 'Retry Request'}
                  </Button>
                )}
                
                <Button
                  onClick={this.handleReset}
                  variant="outline"
                  size="sm"
                >
                  Start Fresh
                </Button>
              </div>
              
              {/* Retry Counter */}
              {this.state.retryCount > 0 && (
                <p className="text-xs opacity-50">
                  Attempt {this.state.retryCount + 1} of {this.getMaxRetries() + 1}
                </p>
              )}

              {/* Error Count Hint */}
              {this.state.errorCount > 1 && (
                <p className="text-xs text-red-700">Multiple AI service errors detected. We’re monitoring.</p>
              )}

              {/* Fallback Options for critical/config errors */}
              {this.props.showFallbackOptions && (this.state.errorType === 'invalid_response' || this.state.errorType === 'ai_overload' || this.state.errorType === 'rate_limit') && (
                <div className="flex gap-2 pt-1">
                  <Button variant="outline" size="sm">Try Alternative</Button>
                  <Button variant="outline" size="sm">Manual Mode</Button>
                </div>
              )}
              
              {/* Development Info */}
              {isDev && this.state.error && (
                <details className="mt-4 text-left w-full">
                  <summary className="cursor-pointer text-xs text-gray-600 hover:text-gray-800">
                    Developer Info
                  </summary>
                  <div className="mt-2 p-2 bg-gray-100 rounded text-xs font-mono">
                    <p><strong>Error Type:</strong> {this.state.errorType}</p>
                    <p><strong>AI Provider:</strong> {this.props.aiProvider || 'unknown'}</p>
                    <p><strong>Message:</strong> {this.state.error.message}</p>
                    <p><strong>Retries:</strong> {this.state.retryCount}/{this.getMaxRetries()}</p>
                    {this.state.error.stack && (
                      <pre className="mt-2 text-xs whitespace-pre-wrap">
                        {this.state.error.stack.slice(0, 500)}...
                      </pre>
                    )}
                  </div>
                </details>
              )}
              
              {/* User Education */}
              <div className="mt-4 p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
                <p>
                  💡 <strong>About AI Services:</strong> AI responses can sometimes be unpredictable. 
                  We use multiple safety measures and fallback strategies to ensure the best experience.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    return this.props.children;
  }
}
